import { useCallback, useEffect, useRef, useState } from "react";
import { CONFIG_MAPA_POR_DEFECTO, type ConfigMapa } from "../grafo/grafoSegunConfig";

/**
 * La configuración del mapa, guardada POR MARCA en localStorage.
 *
 * Por marca y no por usuario: el circuito describe cómo trabaja UNA marca
 * (una puede querer que el sistema genere y otra sólo el plan), y la pantalla
 * ya cambia de marca en caliente vía `useBrandProfile`. La clave lleva el id
 * de la marca adentro, así que cambiar de marca es leer OTRA clave — no queda
 * forma de que la config de una se muestre sobre el circuito de otra.
 *
 * Todavía no hay backend para esto: es preferencia local de una pantalla que
 * hoy es exploratoria (el Bloque A del Estudio de marketing es vista de
 * ejemplo). Cuando exista la tabla, este archivo es el único lugar a cambiar
 * — la pantalla sólo conoce `useConfigMapa`.
 */

const PREFIJO = "atlas:estudio-marketing:config-mapa:";

export const claveConfigMapa = (brandId: string) => `${PREFIJO}${brandId}`;

/**
 * Interpreta lo guardado. PURA (recibe el string crudo) y BLINDADA: cualquier
 * cosa que no sea el JSON esperado cae a los valores por defecto, campo por
 * campo. Un `localStorage` corrupto —una versión vieja del formato, otra
 * pestaña escribiendo, alguien tocando el DevTools— no puede tumbar la
 * pantalla del mapa.
 */
export function leerConfigMapa(crudo: string | null): ConfigMapa {
  if (!crudo) return CONFIG_MAPA_POR_DEFECTO;
  let dato: unknown;
  try {
    dato = JSON.parse(crudo);
  } catch {
    return CONFIG_MAPA_POR_DEFECTO;
  }
  if (typeof dato !== "object" || dato === null || Array.isArray(dato)) {
    return CONFIG_MAPA_POR_DEFECTO;
  }
  const guardado = dato as Record<string, unknown>;
  const bool = (valor: unknown, porDefecto: boolean) =>
    typeof valor === "boolean" ? valor : porDefecto;
  return {
    generacionDeContenido: bool(
      guardado.generacionDeContenido,
      CONFIG_MAPA_POR_DEFECTO.generacionDeContenido,
    ),
    publicacionAutomatica: bool(
      guardado.publicacionAutomatica,
      CONFIG_MAPA_POR_DEFECTO.publicacionAutomatica,
    ),
  };
}

export const mismaConfigMapa = (a: ConfigMapa, b: ConfigMapa) =>
  a.generacionDeContenido === b.generacionDeContenido &&
  a.publicacionAutomatica === b.publicacionAutomatica;

/** Sin marca todavía no hay clave: se arranca en los valores por defecto. El
 *  `try` cubre el storage deshabilitado (Safari privado, iframes con cookies
 *  bloqueadas), donde `getItem` no devuelve null sino que TIRA. */
function leerDelStorage(brandId: string | null): ConfigMapa {
  if (!brandId) return CONFIG_MAPA_POR_DEFECTO;
  try {
    return leerConfigMapa(window.localStorage.getItem(claveConfigMapa(brandId)));
  } catch {
    return CONFIG_MAPA_POR_DEFECTO;
  }
}

/** El `try` cubre lo mismo que la lectura: storage lleno o deshabilitado. El
 *  cambio vale para esta sesión y no sobrevive al reload — preferible a romper
 *  el clic del interruptor. */
function escribirEnStorage(brandId: string, config: ConfigMapa) {
  try {
    window.localStorage.setItem(claveConfigMapa(brandId), JSON.stringify(config));
  } catch {
    /* sin storage: el cambio vive lo que vive la pestaña */
  }
}

/**
 * La config de la marca activa + cómo guardarla.
 *
 * `brandId` puede llegar `null` (el perfil todavía está cargando, o la cuenta
 * no tiene marcas): eso NO es un error, es el estado de arranque. Se muestran
 * los valores por defecto y no se persiste nada — no habría clave con la que
 * hacerlo, y guardar bajo una clave genérica sería exactamente el arrastre
 * entre marcas que este hook evita. Cuando el id llega, el efecto levanta la
 * config de ESA marca.
 *
 * LA ELECCIÓN HECHA MIENTRAS NO HABÍA MARCA NO SE TIRA. La pantalla del mapa
 * se dibuja entera —panel incluido— antes de que `useBrandProfile` resuelva el
 * id: son varios round trips a Supabase encadenados, y en una conexión lenta
 * ahí entran dos clics de sobra. Lo que pasaba antes: el cambio se veía (el
 * circuito se redibujaba), no se escribía, y cuando llegaba el id el efecto
 * leía la clave de esa marca y PISABA la elección — la pastilla saltaba sola
 * y el anillo se rearmaba, sin nada que distinguiera "guardado" de
 * "descartado". Ahora el cambio hecho sin marca queda pendiente y, apenas
 * llega el id, se persiste en la clave de esa marca en vez de perderse: es lo
 * último que pidió el usuario, así que le gana a lo que hubiera guardado antes.
 */
export function useConfigMapa(brandId: string | null): [ConfigMapa, (nueva: ConfigMapa) => void] {
  const [config, setConfig] = useState<ConfigMapa>(() => leerDelStorage(brandId));
  /** Lo que el usuario eligió mientras `brandId` todavía era `null`. */
  const pendienteSinMarca = useRef<ConfigMapa | null>(null);

  useEffect(() => {
    if (brandId && pendienteSinMarca.current) {
      // Llegó la marca y hay una elección esperando: se escribe y NO se pisa
      // el estado (ya es el que el usuario ve en pantalla).
      escribirEnStorage(brandId, pendienteSinMarca.current);
      pendienteSinMarca.current = null;
      return;
    }
    // Cambió la marca activa ⇒ la config es otra. Sin esto el panel seguiría
    // mostrando —y el circuito dibujando— la de la marca anterior.
    // Se compara antes de setear para no cambiar la identidad del objeto al
    // pedo: de ella cuelga el `useMemo` que deriva el grafo.
    const guardada = leerDelStorage(brandId);
    setConfig((previa) => (mismaConfigMapa(previa, guardada) ? previa : guardada));
  }, [brandId]);

  const guardar = useCallback(
    (nueva: ConfigMapa) => {
      setConfig(nueva);
      if (!brandId) {
        // Sin clave con la que persistir. Queda anotado para escribirlo apenas
        // haya marca; si nunca la hay (cuenta sin marcas), vale para la sesión.
        pendienteSinMarca.current = nueva;
        return;
      }
      escribirEnStorage(brandId, nueva);
    },
    [brandId],
  );

  return [config, guardar];
}
